// The onboarding flow model: an ordered list of step nodes, as data.
//
// A product's onboarding order, gating and copy live in a JSON file of this
// shape; code only says how each `kind` renders (see registry.ts). Parsing is
// strict and pure so a malformed flow fails in a deno test, not in a browser.

export type Surface = "mobile" | "desktop";
export const SURFACES: readonly Surface[] = ["mobile", "desktop"];

/** `step` sits in the carousel with the nav row; `standalone` is shown alone,
 * without nav, in the compact frame (a terminal notice or a mobile bridge). */
export type Presentation = "step" | "standalone";
export const PRESENTATIONS: readonly Presentation[] = ["step", "standalone"];

export type StepGate = {
  /** Show only on these surfaces. */
  surface?: Surface | Surface[];
  /** Every named fact must be true. Unknown facts are false. */
  when?: string | string[];
  /** No named fact may be true. */
  unless?: string | string[];
};

export type StepNode = {
  id: string;
  kind: string;
  props?: Record<string, unknown>;
  gate?: StepGate;
  presentation?: Presentation;
};

export type FlowLabels = {
  back?: string;
  next?: string;
  finish?: string;
};

export type FlowModel = {
  schema: typeof FlowModel.SCHEMA;
  name: string;
  labels?: FlowLabels;
  steps: StepNode[];
};

export class FlowModelError extends Error {
  constructor(
    readonly path: string,
    message: string,
  ) {
    super(`${path}: ${message}`);
    this.name = "FlowModelError";
  }
}

export namespace FlowModel {
  export const SCHEMA = "tento.onboarding.flow/v1" as const;
  export const STEP_ID = /^[a-z0-9][a-z0-9-]*$/;
  export const DEFAULT_LABELS: Required<FlowLabels> = {
    back: "Back",
    next: "Continue",
    finish: "Finish",
  };

  /** Validate an untrusted value (usually imported JSON) into a FlowModel.
   * Throws FlowModelError naming the first offending path. */
  export function parse(value: unknown): FlowModel {
    const root = object(value, "$");
    for (const key of Object.keys(root)) {
      if (!["$schema", "schema", "name", "labels", "steps"].includes(key)) {
        throw new FlowModelError(`$.${key}`, "unknown field");
      }
    }
    if (root.schema !== SCHEMA) {
      throw new FlowModelError("$.schema", `expected "${SCHEMA}"`);
    }
    const name = nonEmptyString(root.name, "$.name");
    const labels = root.labels == null ? undefined : parseLabels(root.labels);
    if (!Array.isArray(root.steps)) {
      throw new FlowModelError("$.steps", "expected an array");
    }
    const seen = new Set<string>();
    const steps = root.steps.map((raw, i) => {
      const step = parseStep(raw, `$.steps[${i}]`);
      if (seen.has(step.id)) {
        throw new FlowModelError(
          `$.steps[${i}].id`,
          `duplicate step id "${step.id}"`,
        );
      }
      seen.add(step.id);
      return step;
    });
    const model: FlowModel = { schema: SCHEMA, name, steps };
    if (labels) model.labels = labels;
    return model;
  }

  /** Kinds the model names that the registry cannot build. Separate from
   * parse: a model is well-formed on its own; whether its kinds exist is a
   * property of the bundle that loads it. */
  export function validateKinds(
    model: FlowModel,
    registry: { has(kind: string): boolean },
  ): string[] {
    return model.steps
      .filter((s) => !registry.has(s.kind))
      .map((s) => `step "${s.id}": unknown kind "${s.kind}"`);
  }

  export function labels(model: FlowModel): Required<FlowLabels> {
    return { ...DEFAULT_LABELS, ...(model.labels ?? {}) };
  }

  function parseStep(raw: unknown, path: string): StepNode {
    const o = object(raw, path);
    for (const key of Object.keys(o)) {
      if (!["id", "kind", "props", "gate", "presentation"].includes(key)) {
        throw new FlowModelError(`${path}.${key}`, "unknown field");
      }
    }
    const id = nonEmptyString(o.id, `${path}.id`);
    if (!STEP_ID.test(id)) {
      throw new FlowModelError(
        `${path}.id`,
        `"${id}" must match ${STEP_ID.source}`,
      );
    }
    const step: StepNode = { id, kind: nonEmptyString(o.kind, `${path}.kind`) };
    if (o.props != null) step.props = object(o.props, `${path}.props`);
    if (o.gate != null) step.gate = parseGate(o.gate, `${path}.gate`);
    if (o.presentation != null) {
      if (!PRESENTATIONS.includes(o.presentation as Presentation)) {
        throw new FlowModelError(
          `${path}.presentation`,
          `expected one of ${PRESENTATIONS.join(", ")}`,
        );
      }
      step.presentation = o.presentation as Presentation;
    }
    return step;
  }

  function parseGate(raw: unknown, path: string): StepGate {
    const o = object(raw, path);
    const gate: StepGate = {};
    for (const key of Object.keys(o)) {
      if (!["surface", "when", "unless"].includes(key)) {
        throw new FlowModelError(`${path}.${key}`, "unknown field");
      }
    }
    if (o.surface != null) {
      const list = stringOrList(o.surface, `${path}.surface`);
      for (const s of list) {
        if (!SURFACES.includes(s as Surface)) {
          throw new FlowModelError(
            `${path}.surface`,
            `unknown surface "${s}"`,
          );
        }
      }
      gate.surface = o.surface as Surface | Surface[];
    }
    if (o.when != null) {
      stringOrList(o.when, `${path}.when`);
      gate.when = o.when as string | string[];
    }
    if (o.unless != null) {
      stringOrList(o.unless, `${path}.unless`);
      gate.unless = o.unless as string | string[];
    }
    return gate;
  }

  function parseLabels(raw: unknown): FlowLabels {
    const o = object(raw, "$.labels");
    const labels: FlowLabels = {};
    for (const key of Object.keys(o)) {
      if (key !== "back" && key !== "next" && key !== "finish") {
        throw new FlowModelError(`$.labels.${key}`, "unknown label");
      }
      labels[key] = nonEmptyString(o[key], `$.labels.${key}`);
    }
    return labels;
  }

  function object(value: unknown, path: string): Record<string, unknown> {
    if (value == null || typeof value !== "object" || Array.isArray(value)) {
      throw new FlowModelError(path, "expected an object");
    }
    return value as Record<string, unknown>;
  }

  function nonEmptyString(value: unknown, path: string): string {
    if (typeof value !== "string" || value.length === 0) {
      throw new FlowModelError(path, "expected a non-empty string");
    }
    return value;
  }

  function stringOrList(value: unknown, path: string): string[] {
    const list = Array.isArray(value) ? value : [value];
    if (list.length === 0) {
      throw new FlowModelError(path, "expected a string or non-empty list");
    }
    for (const item of list) nonEmptyString(item, path);
    return list as string[];
  }
}
